/**
 * src/hooks/use-database.ts — every React Query hook the app has.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/hooks/use-database.ts` (444 lines) **plus**
 * `src/hooks/use-admin.ts` (92 lines), merged here — the two files were byte-identical in
 * structure and duplicated `getAuthHeaders()` verbatim (spec §3 opening paragraph). The merge is
 * organisational only: every key, endpoint, `enabled` predicate, invalidation set, thrown message
 * and toast string below is the source's.
 * Spec: `docs/spec/06-frontend-components.md` §3.1 (queries), §3.2 (mutations), §3.3 (invalidation
 * correctness analysis), §3.4 (backend shape facts), §15 D-5/D-9/D-10/D-12, §16 ranks 5/10/16.
 *
 * ⚠️ NOTE FOR COMPONENT AUTHORS: the source's admin hooks lived in `@/hooks/use-admin`. In L2HC
 * they are at the bottom of THIS file. `src/pages/Admin.tsx` must import `usePendingUsers`,
 * `useApproveUser`, `useRejectUser` from `@/hooks/use-database`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE INVALIDATION KEYS ARE WRONG ON PURPOSE — DF-22 rank 10
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * React Query matches invalidation keys by **array prefix**. Working through spec §3.3:
 *
 *   ['documents'] ⊃ ['documents', userId] and ['documents','by-folder',…]   ✅ both refresh
 *   ['preambles'] ⊃ ['preambles', userId] and ['preambles','by-folder',…]   ✅ both refresh
 *   ['folders']   ⊃ ['folders', userId] and ['folders','by-parent',…]
 *                 ⊅ ['folder', id]   ← `'folders' !== 'folder'`             ❌
 *
 * So `useUpdateFolder` invalidating `['folders']` alone **never refreshes the breadcrumb**, which
 * reads `useFolder(...)` → `['folder', id]` (consumed at `EnhancedSavedDocuments.tsx:66-67`).
 * Rename a folder and the breadcrumb keeps the old name until something else evicts the entry.
 * And `useDeleteFolder` invalidates `['folders']` + `['documents']` but **not** `['preambles']`,
 * although the schema re-parents preambles to Root on folder delete (`ON DELETE SET NULL`), so the
 * preamble list goes stale.
 *
 * **Getting invalidation "right" here is a FIX, and fixes are forbidden in a port commit**
 * (CLAUDE.md §3). Adding `['folder']` or `['preambles']` to those two `onSuccess` blocks is a
 * one-line change that will have to be reverted. Both sites carry an explicit marker below.
 *
 * ═══ OTHER DF-22 DEFECTS THIS FILE CARRIES ════════════════════════════════════════════════════
 *  - **rank 5** every query and every mutation but one throws a bare `new Error('Failed to …')`
 *    and discards the server's `{ error }` body (`src/lib/api.ts#apiRequest`). No hook exposes a
 *    retry affordance and no hook handles 401/403. Components must NOT render `isError`.
 *  - **rank 16** `is_default` on a preamble is write-only. There is deliberately no hook that reads
 *    the flag back or auto-loads the default preamble. Do not add one.
 *  - **rank 7** `useUpdateFolder` cheerfully toasts `'Folder updated'` for a move-to-Root that the
 *    Worker's `COALESCE(?, parent_id)` turns into a no-op.
 *  - **rank 2** `useUpdateDocument` exists and works, but `ConverterApp` never routes a save
 *    through it (`ConverterApp.tsx:256-259` never passes `documentId`), so saving a loaded document
 *    creates a duplicate. Its only live callers are `MoveDialog` and the sidebar's drag handler.
 *
 * ═══ TOASTS ═══════════════════════════════════════════════════════════════════════════════════
 * The source raised all of these through shadcn's `useToast`. Ported to `sonner` — the ONE approved
 * deviation (DF-22 rank 9); see the header of `src/contexts/AuthContext.tsx` for the mapping table
 * and its two visible consequences. Titles and descriptions are byte-identical to the source.
 *
 * Note these toasts are *additional* to the ones the dialogs raise: `SaveDocumentDialog.tsx:148`
 * toasts `'Document saved successfully'` while `useCreateDocument` below toasts `'Document saved'`.
 * Two toasts per save is the shipped behaviour (spec §12) and stays.
 *
 * ═══ SHARED WORKSPACE ═════════════════════════════════════════════════════════════════════════
 * `user?.id` appears in most query keys and is **cosmetic** — every list endpoint is unscoped
 * (CLAUDE.md §5, spec §3.4). It is kept because removing it would change cache identity across a
 * user switch, which is observable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { apiRequest } from '@/lib/api'
import type {
  ApproveUserResponse,
  CreateDocumentInput,
  CreateFolderInput,
  CreatePreambleInput,
  Document,
  DocumentByFolderItem,
  DocumentDetail,
  DocumentListItem,
  Folder,
  MessageResponse,
  Preamble,
  PreambleListItem,
  UpdateDocumentInput,
  UpdateFolderInput,
  UpdatePreambleInput,
  User,
} from '@/lib/types'

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Documents
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * `GET /api/documents` — `use-database.ts:17-31`.
 * Key `['documents', user?.id]`. Ordered `updated_at DESC` by the Worker.
 *
 * Returns `preamble_name` and `folder_name`; the sidebar renders `doc.preamble_name`
 * (`EnhancedSavedDocuments.tsx:241`). Not consumed by the shipped sidebar, which uses
 * {@link useDocumentsByFolder}; live callers are the legacy `SavedDocuments.tsx` only (spec §13).
 */
export const useDocuments = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['documents', user?.id],
    queryFn: () =>
      apiRequest<DocumentListItem[]>('/api/documents', {
        errorMessage: 'Failed to fetch documents',
      }),
    enabled: !!user,
  })
}

/**
 * `GET /api/documents/:id` — `use-database.ts:33-47`. Key `['document', documentId]`.
 *
 * **Dead export** — never called by any component (spec §3.1, §13). Ported because
 * {@link useUpdateDocument} invalidates `['document']`, and removing this would make that
 * invalidation look like dead code when it is in fact the source's exact behaviour.
 *
 * `enabled: !!user && !!documentId` — id `0` disables the query.
 */
export const useDocument = (documentId: number) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['document', documentId],
    queryFn: () =>
      apiRequest<DocumentDetail>(`/api/documents/${documentId}`, {
        errorMessage: 'Failed to fetch document',
      }),
    enabled: !!user && !!documentId,
  })
}

/**
 * `GET /api/documents/by-folder/{id|'root'}` — `use-database.ts:317-331`.
 * Key `['documents', 'by-folder', folderId, user?.id]`.
 *
 * `null` → the literal path segment `root`. The response has **no `folder_name`** — the shape
 * differs from {@link useDocuments} on purpose (spec §3.4).
 *
 * Two live consumers: the sidebar's document list (`EnhancedSavedDocuments.tsx:62`) and
 * `SaveDocumentDialog`'s duplicate-title check (`:61`). The dialog gates that check on
 * `&& documentsInFolder`, so saving before this query resolves skips the overwrite prompt entirely
 * and creates a silent duplicate (spec §15 D-8). Not this layer's to fix — do not add
 * `isFetching`-based gating here.
 */
export const useDocumentsByFolder = (folderId: number | null) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['documents', 'by-folder', folderId, user?.id],
    queryFn: () =>
      apiRequest<DocumentByFolderItem[]>(
        `/api/documents/by-folder/${folderId === null ? 'root' : folderId}`,
        { errorMessage: 'Failed to fetch documents' },
      ),
    enabled: !!user,
  })
}

/**
 * `POST /api/documents` — `use-database.ts:49-78`. Invalidates `['documents']` (prefix-matches both
 * document list keys — correct, spec §3.3).
 *
 * DF-20: `html_content` is **not** in {@link CreateDocumentInput} and must not be sent.
 */
export const useCreateDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDocumentInput) =>
      apiRequest<Document>('/api/documents', {
        method: 'POST',
        body: data,
        errorMessage: 'Failed to create document',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast('Document saved', {
        description: 'Your document has been saved successfully',
      })
    },
    onError: (error) => {
      toast.error('Error saving document', {
        description: error instanceof Error ? error.message : 'Failed to save document',
      })
    },
  })
}

/**
 * `PUT /api/documents/:id` — `use-database.ts:80-110`.
 * Invalidates `['documents']` **and** `['document']`.
 *
 * DF-22 rank 2: unreachable from the save dialog, because `ConverterApp.tsx:256-259` never passes
 * `documentId`. Its live callers are `MoveDialog.tsx:85` and `EnhancedSavedDocuments.tsx:108/115`,
 * both sending only `{ folder_id }`.
 */
export const useUpdateDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: UpdateDocumentInput }) =>
      apiRequest<Document>(`/api/documents/${id}`, {
        method: 'PUT',
        body: updates,
        errorMessage: 'Failed to update document',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document'] })
      toast('Document updated', {
        description: 'Your changes have been saved',
      })
    },
    onError: (error) => {
      toast.error('Error updating document', {
        description: error instanceof Error ? error.message : 'Failed to update document',
      })
    },
  })
}

/** `DELETE /api/documents/:id` — `use-database.ts:112-140`. Invalidates `['documents']`. */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: number) =>
      apiRequest<MessageResponse>(`/api/documents/${documentId}`, {
        method: 'DELETE',
        errorMessage: 'Failed to delete document',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast('Document deleted', {
        description: 'The document has been removed',
      })
    },
    onError: (error) => {
      toast.error('Error deleting document', {
        description: error instanceof Error ? error.message : 'Failed to delete document',
      })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Preambles
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * `GET /api/preambles` — `use-database.ts:143-157`. Key `['preambles', user?.id]`.
 * Ordered `created_at DESC` by the Worker — not `updated_at`, unlike documents (spec §3.4).
 *
 * Live consumer: `SaveDocumentDialog`'s preamble dropdown (`:59`), which renders
 * `preamble.name` + `' (Default)'` when `is_default`. That badge is the *only* thing that reads the
 * flag — DF-22 rank 16.
 */
export const usePreambles = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['preambles', user?.id],
    queryFn: () =>
      apiRequest<PreambleListItem[]>('/api/preambles', {
        errorMessage: 'Failed to fetch preambles',
      }),
    enabled: !!user,
  })
}

/**
 * `GET /api/preambles/:id` — `use-database.ts:159-173`. Key `['preamble', preambleId]`.
 *
 * **Dead export** (spec §3.1, §13), kept for the same reason as {@link useDocument}:
 * {@link useUpdatePreamble} invalidates `['preamble']`.
 */
export const usePreamble = (preambleId: number) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['preamble', preambleId],
    queryFn: () =>
      apiRequest<Preamble>(`/api/preambles/${preambleId}`, {
        errorMessage: 'Failed to fetch preamble',
      }),
    enabled: !!user && !!preambleId,
  })
}

/**
 * `GET /api/preambles/by-folder/{id|'root'}` — `use-database.ts:333-347`.
 * Key `['preambles', 'by-folder', folderId, user?.id]`. Returns bare rows: **no `folder_name`**.
 *
 * Worker quirk that constrains this call site: the preambles router accepts **only** `'root'` for
 * the null-folder branch, where documents and folders also accept `'null'`
 * (`worker/routes/preambles.ts:158-166`). The `folderId === null ? 'root' : folderId` expression
 * below is therefore load-bearing — do not "simplify" it to `?? 'null'`.
 */
export const usePreamblesByFolder = (folderId: number | null) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['preambles', 'by-folder', folderId, user?.id],
    queryFn: () =>
      apiRequest<Preamble[]>(
        `/api/preambles/by-folder/${folderId === null ? 'root' : folderId}`,
        { errorMessage: 'Failed to fetch preambles' },
      ),
    enabled: !!user,
  })
}

/** `POST /api/preambles` — `use-database.ts:175-204`. Invalidates `['preambles']`. */
export const useCreatePreamble = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePreambleInput) =>
      apiRequest<Preamble>('/api/preambles', {
        method: 'POST',
        body: data,
        errorMessage: 'Failed to create preamble',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preambles'] })
      toast('Preamble saved', {
        description: 'Your preamble has been saved successfully',
      })
    },
    onError: (error) => {
      toast.error('Error saving preamble', {
        description: error instanceof Error ? error.message : 'Failed to save preamble',
      })
    },
  })
}

/**
 * `PUT /api/preambles/:id` — `use-database.ts:206-236`.
 * Invalidates `['preambles']` **and** `['preamble']`.
 *
 * DF-22 rank 2 again: unreachable from `SavePreambleDialog`, because `ConverterApp.tsx:255` never
 * passes `preambleId`. Live callers are `MoveDialog.tsx:90` and `EnhancedSavedDocuments.tsx:115`.
 */
export const useUpdatePreamble = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: UpdatePreambleInput }) =>
      apiRequest<Preamble>(`/api/preambles/${id}`, {
        method: 'PUT',
        body: updates,
        errorMessage: 'Failed to update preamble',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preambles'] })
      queryClient.invalidateQueries({ queryKey: ['preamble'] })
      toast('Preamble updated', {
        description: 'Your changes have been saved',
      })
    },
    onError: (error) => {
      toast.error('Error updating preamble', {
        description: error instanceof Error ? error.message : 'Failed to update preamble',
      })
    },
  })
}

/** `DELETE /api/preambles/:id` — `use-database.ts:238-266`. Invalidates `['preambles']`. */
export const useDeletePreamble = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (preambleId: number) =>
      apiRequest<MessageResponse>(`/api/preambles/${preambleId}`, {
        method: 'DELETE',
        errorMessage: 'Failed to delete preamble',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preambles'] })
      toast('Preamble deleted', {
        description: 'The preamble has been removed',
      })
    },
    onError: (error) => {
      toast.error('Error deleting preamble', {
        description: error instanceof Error ? error.message : 'Failed to delete preamble',
      })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Folders
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * `GET /api/folders` — `use-database.ts:269-283`. Key `['folders', user?.id]`.
 *
 * A **flat** list; `FolderTree.buildFolderTree` assembles the hierarchy client-side on every render
 * and silently drops any node whose `parent_id` is not in the map (`FolderTree.tsx:250-259`) —
 * which is how a drag-created cycle makes folders disappear (DF-22 rank 8).
 *
 * The Worker sorts with `Intl.Collator('fr')` to preserve the accent-aware ordering Postgres gave
 * (`worker/routes/folders.ts:51-69`); nothing is re-sorted here.
 */
export const useFolders = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['folders', user?.id],
    queryFn: () =>
      apiRequest<Folder[]>('/api/folders', {
        errorMessage: 'Failed to fetch folders',
      }),
    enabled: !!user,
  })
}

/**
 * `GET /api/folders/:id` — `use-database.ts:285-299`. Key **`['folder', folderId]`** (singular).
 *
 * ⚠️ **DF-22 rank 10 lives on this key.** `useUpdateFolder` invalidates `['folders']` (plural),
 * which does not prefix-match `['folder', id]`, so this entry is never refreshed after a rename or
 * a move. The sidebar breadcrumb reads exactly this hook (`EnhancedSavedDocuments.tsx:66-67`) and
 * therefore keeps showing the old folder name.
 *
 * Do NOT rename the key to `['folders', 'detail', id]` to make the prefix match. That is the fix.
 *
 * `enabled: !!user && !!folderId` — and the sidebar calls `useFolder(selectedFolder || 0)`, so at
 * Root the id is `0` and the query is disabled. That is why the breadcrumb reads `'Root'` from a
 * ternary rather than from data.
 */
export const useFolder = (folderId: number) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['folder', folderId],
    queryFn: () =>
      apiRequest<Folder>(`/api/folders/${folderId}`, {
        errorMessage: 'Failed to fetch folder',
      }),
    enabled: !!user && !!folderId,
  })
}

/**
 * `GET /api/folders/by-parent/{id|'root'}` — `use-database.ts:301-315`.
 * Key `['folders', 'by-parent', parentId, user?.id]`.
 *
 * **Dead export** — never called by any component (spec §3.1, §13). `FolderTree` fetches the flat
 * list via {@link useFolders} and builds the tree itself.
 */
export const useFoldersByParent = (parentId: number | null) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['folders', 'by-parent', parentId, user?.id],
    queryFn: () =>
      apiRequest<Folder[]>(
        `/api/folders/by-parent/${parentId === null ? 'root' : parentId}`,
        { errorMessage: 'Failed to fetch folders' },
      ),
    enabled: !!user,
  })
}

/**
 * `POST /api/folders` — `use-database.ts:349-378`. Invalidates `['folders']`.
 *
 * `FolderTree.tsx:291` sends the **untrimmed** name (DF-22 rank 17): `'  x  '` is stored with its
 * spaces, because the `.trim()` there feeds the validity check only. No trimming happens here
 * either — adding it would be the fix.
 */
export const useCreateFolder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateFolderInput) =>
      apiRequest<Folder>('/api/folders', {
        method: 'POST',
        body: data,
        errorMessage: 'Failed to create folder',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      toast('Folder created', {
        description: 'Your folder has been created successfully',
      })
    },
    onError: (error) => {
      toast.error('Error creating folder', {
        description: error instanceof Error ? error.message : 'Failed to create folder',
      })
    },
  })
}

/**
 * `PUT /api/folders/:id` — `use-database.ts:380-409`. Used for rename **and** for folder moves
 * (`MoveDialog.tsx:80`, `EnhancedSavedDocuments.tsx:131`).
 *
 * ⚠️⚠️ **DF-22 rank 10 — THE INVALIDATION BELOW IS INCOMPLETE ON PURPOSE.**
 * `['folders']` only. It does not invalidate `['folder']`, so {@link useFolder} — the breadcrumb —
 * never refetches and keeps the pre-rename name. Spec §3.3 and §15 D-9 both call this out.
 * Adding `queryClient.invalidateQueries({ queryKey: ['folder'] })` is the one-line fix. **Do not.**
 *
 * ⚠️ **DF-22 rank 7 also passes through here.** A move-to-Root sends `parent_id: null`, which the
 * Worker's `parent_id = COALESCE(?, parent_id)` silently ignores
 * (`worker/routes/folders.ts:301-313`) while still returning 200 — so the folder does not move and
 * both this hook and the caller toast success. Documents/preambles do not have this problem.
 */
export const useUpdateFolder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: UpdateFolderInput }) =>
      apiRequest<Folder>(`/api/folders/${id}`, {
        method: 'PUT',
        body: updates,
        errorMessage: 'Failed to update folder',
      }),
    onSuccess: () => {
      // DF-22 rank 10 — `['folder']` is deliberately absent. See the doc comment above.
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      toast('Folder updated', {
        description: 'Your changes have been saved',
      })
    },
    onError: (error) => {
      toast.error('Error updating folder', {
        description: error instanceof Error ? error.message : 'Failed to update folder',
      })
    },
  })
}

/**
 * `DELETE /api/folders/:id` — `use-database.ts:411-443`.
 *
 * **The one call site in the whole app that reads the server's error body** — `error.error ||
 * 'Failed to delete folder'` (`:421-424`). Everything else discards it (DF-22 rank 5). Reproduced
 * via `apiRequest`'s `readErrorBody` flag, which exists for this hook alone.
 *
 * ⚠️ **DF-22 rank 10, second half.** Invalidates `['folders']` and `['documents']` but **not**
 * `['preambles']`, although the schema moves preambles inside the folder back to Root
 * (`folder_id ON DELETE SET NULL`). The preamble list therefore renders stale entries pointing at a
 * folder that no longer exists. Adding `['preambles']` is the fix (spec §15 D-10). **Do not.**
 */
export const useDeleteFolder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (folderId: number) =>
      apiRequest<MessageResponse>(`/api/folders/${folderId}`, {
        method: 'DELETE',
        errorMessage: 'Failed to delete folder',
        readErrorBody: true,
      }),
    onSuccess: () => {
      // DF-22 rank 10 — `['preambles']` is deliberately absent. See the doc comment above.
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast('Folder deleted', {
        description: 'The folder has been removed',
      })
    },
    onError: (error) => {
      toast.error('Error deleting folder', {
        description: error instanceof Error ? error.message : 'Failed to delete folder',
      })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Admin — ported from `src/hooks/use-admin.ts` (92 lines)
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * `GET /api/admin/pending-users` — `use-admin.ts:17-31`. Key `['admin', 'pending-users']`.
 *
 * The only query key in the app with **no `user?.id` segment**, and the only one gated on a role:
 * `enabled: !!user && user.role === 'admin'`.
 *
 * `Admin.tsx:50` renders `'Failed to load users'` on `isError` — the single error state anywhere in
 * the app (spec §11). It stays the only one; do not generalise it into the sidebar (DF-22 rank 5).
 *
 * ⚠️ Each `User` here carries `created_at: string | null`, and `Admin.tsx:63` must format it with
 * **no guard** — `format(new Date(user.created_at as string), 'PPP p')` — so the `RangeError` that
 * whites out the app (DF-22 rank 6) is reproducible. Cast; do not check. See `src/lib/types.ts`.
 *
 * Note the Worker filters `WHERE is_approved = 0`, which under three-valued logic does not match
 * `NULL`, so a NULL-approval account is invisible here *and* blocked at login — permanently
 * stranded (Worker defect D-32). Reproduced server-side; nothing to do here.
 */
export const usePendingUsers = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['admin', 'pending-users'],
    queryFn: () =>
      apiRequest<User[]>('/api/admin/pending-users', {
        errorMessage: 'Failed to fetch pending users',
      }),
    enabled: !!user && user.role === 'admin',
  })
}

/**
 * `PUT /api/admin/users/:id/approve` — `use-admin.ts:33-61`. **No request body** (the Worker reads
 * none). Invalidates `['admin', 'pending-users']`.
 *
 * DF-22 rank 18 note for the Admin page: `Admin.tsx:70-86` binds both buttons' `disabled` and
 * spinner to this shared mutation's `isPending`, so approving one row disables Approve on **every**
 * row (spec §15 D-20). Reproduce that — do not track an in-flight user id.
 */
export const useApproveUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) =>
      apiRequest<ApproveUserResponse>(`/api/admin/users/${userId}/approve`, {
        method: 'PUT',
        errorMessage: 'Failed to approve user',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      toast('User Approved', {
        description: 'The user can now log in.',
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to approve user',
      })
    },
  })
}

/**
 * `DELETE /api/admin/users/:id` — `use-admin.ts:63-91`. Invalidates `['admin', 'pending-users']`.
 *
 * ⚠️ **This comment described DF-16 and is now out of date in its most important claim.** It used to
 * say that rejecting a user destroys every document, preamble and folder they created, because
 * `user_id` was `ON DELETE CASCADE`. **Migration `0002` changed all three to `ON DELETE SET NULL`**
 * (2026-08-09), so a rejection now destroys **no content at all** — the rows survive with a null
 * creator, and nothing reads `user_id` anyway.
 *
 * What is still true: the only guard here is the native `confirm()` in `Admin.tsx`, with no
 * affected-item count. That guard is part of the ported screen and stays exactly as it is — do not
 * upgrade it to an `AlertDialog`. The Team section's delete ({@link useDeleteUser}) is the new,
 * separately-authorised path and is not bound by that.
 */
export const useRejectUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) =>
      apiRequest<MessageResponse>(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        errorMessage: 'Failed to reject user',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      toast('User Rejected', {
        description: 'The user account has been removed.',
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to reject user',
      })
    },
  })
}

// ---------------------------------------------------------------------------
// Team management — NEW. Owner request, 2026-08-09. No source counterpart.
// ---------------------------------------------------------------------------

/**
 * `GET /api/admin/users` — **every** user, not only the pending ones.
 *
 * {@link usePendingUsers} above queries `/pending-users`, which the Worker filters to
 * `WHERE is_approved = 0`. That list is the source's and stays exactly as ported; it simply cannot
 * show the people who already use the app, which is who you need to see to change a role.
 *
 * Separate query key from `['admin','pending-users']` on purpose: approving somebody moves them
 * from one list to the other, so both are invalidated together by the mutations below.
 */
export const useAllUsers = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      apiRequest<User[]>('/api/admin/users', {
        errorMessage: 'Failed to fetch users',
      }),
    enabled: !!user && user.role === 'admin',
  })
}

/**
 * `PATCH /api/admin/users/:id/role` — promote to `admin` or demote to `user`.
 *
 * `readErrorBody: true` is deliberate and is the point of this hook's error handling. The Worker
 * refuses in two ways an admin genuinely needs to read — *"There must be at least one
 * administrator"* and *"You cannot remove your own admin access"* — and `apiRequest` otherwise
 * discards the server's message and throws the generic literal (DF-22 rank 5). Those two strings
 * explain a rule; a generic "Failed to change role" would leave someone re-clicking a button that
 * cannot work.
 *
 * Invalidates **both** admin lists: promoting an unapproved user also approves them, which removes
 * them from `pending-users`.
 */
export const useSetUserRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: 'admin' | 'user' }) =>
      apiRequest<{ message: string; user: User }>(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: { role },
        errorMessage: 'Failed to change role',
        readErrorBody: true,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      toast(result.user.role === 'admin' ? 'Administrator added' : 'Administrator removed', {
        description:
          result.user.role === 'admin'
            ? `${result.user.username} can now approve users and manage the team.`
            : `${result.user.username} is now a regular user.`,
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to change role',
      })
    },
  })
}

/**
 * `DELETE /api/admin/users/:id` — remove a team member, **including their Hexclave account**.
 *
 * Owner instruction, 2026-08-14. Distinct from {@link useRejectUser} even though both call the same
 * endpoint, and deliberately so:
 *
 *  - `useRejectUser` belongs to the **ported** Pending Approvals screen, which is still under DF-22
 *    visual validation (`CLAUDE.md` §3). Its toast wording, its single invalidation and its native
 *    `confirm()` are all reproduced behaviour and must not drift.
 *  - This one belongs to the **new** Team section, so it invalidates both admin lists and — the part
 *    that matters — sets `readErrorBody`.
 *
 * `readErrorBody: true` is the point. The Worker refuses in three ways an admin needs to *read*:
 * *"The main administrator cannot be deleted"*, *"There must be at least one administrator"* and
 * *"You cannot delete your own account"*. Without it `apiRequest` discards the server's message and
 * throws the generic literal (DF-22 rank 5), leaving someone re-clicking a button that cannot work.
 *
 * ⚠️ **The account is unrecoverable, though the person is not permanently locked out.** It destroys
 * the Hexclave identity — credentials and OAuth links — not merely their access here. They can sign
 * up again with the same address and be re-approved, but as a new account. Their documents, folders
 * and preambles all survive (migration `0002`). The UI must confirm before calling it, and the
 * confirmation says both halves — an admin who thinks this is permanent hesitates over the wrong thing.
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) =>
      apiRequest<MessageResponse>(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        errorMessage: 'Failed to delete user',
        readErrorBody: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      toast('Account deleted', {
        description: 'The Hexclave account was removed. Their documents were kept.',
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to delete user',
      })
    },
  })
}

/**
 * `PATCH /api/admin/users/:id/profile` — edit another user's name and email **at Hexclave**, mirrored
 * into D1.
 *
 * The endpoint has existed since 2026-08-09 and until now **nothing called it** — the capability was
 * deployed with no way to reach it. This hook is that way.
 *
 * The Worker writes Hexclave first and D1 second, so a refusal changes nothing on either side. It
 * refuses a legacy row (`external_id IS NULL`, no account behind it) and refuses setting anyone's
 * address to `HEXCLAVE_ADMIN_EMAIL` — that address is force-promoted to admin on sign-in, so
 * allowing it would turn "edit a profile" into "mint an admin". `readErrorBody` surfaces all of
 * those, plus Hexclave's own message for a duplicate or malformed address.
 *
 * `role` and `is_approved` are **not** editable here: they are D1's own and have their own endpoints.
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, username, email }: { userId: number; username: string; email: string }) =>
      apiRequest<{ message: string; user: User }>(`/api/admin/users/${userId}/profile`, {
        method: 'PATCH',
        body: { username, email },
        errorMessage: 'Failed to update profile',
        readErrorBody: true,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      toast('Profile updated', {
        description: `${result.user.username} was updated in Hexclave and here.`,
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to update profile',
      })
    },
  })
}
